import React, { useState, useEffect, useRef } from 'react';
import { Search, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '../lib/utils';
import api from '../utils/api';
import { productPath } from '../utils/helpers';

const SearchBar = ({ onSearch, className, initialQuery = '' }) => {
  const [query, setQuery] = useState(initialQuery);
  const [isFocused, setIsFocused] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  // Update query when initialQuery changes
  useEffect(() => {
    setQuery(initialQuery);
  }, [initialQuery]);

  // Fetch suggestions when query changes
  useEffect(() => {
    if (query.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    // Debounce the API call
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await api.get(`/products/suggest?q=${encodeURIComponent(query)}&limit=8`);
        setSuggestions(response.data.suggestions || []);
        setShowSuggestions(true);
        setSelectedIndex(-1);
      } catch (error) {
        console.error('Error fetching suggestions:', error);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowSuggestions(false);
    onSearch(query);
  };

  const handleClear = () => {
    setQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
    onSearch('');
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault();
      const selected = suggestions[selectedIndex];
      window.location.href = productPath(selected);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(price);
  };

  return (
    <div ref={wrapperRef} className={cn("relative w-full max-w-2xl mx-auto", className)}>
      <form onSubmit={handleSubmit}>
        <div className={cn(
          "relative flex items-center border bg-white transition-all duration-300 rounded",
          isFocused ? "border-brand-cobalt shadow-brand-cobalt" : "border-brand-black/15"
        )}>
          <Search className="absolute left-4 text-brand-black/40" size={20} />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => {
              setIsFocused(true);
              if (suggestions.length > 0) setShowSuggestions(true);
            }}
            onBlur={() => setIsFocused(false)}
            onKeyDown={handleKeyDown}
            placeholder="Buscar por código, marca, o nombre del perfume..."
            className="w-full pl-12 pr-12 py-4 text-body-md focus:outline-none bg-transparent text-brand-black placeholder:text-brand-black/40"
            data-testid="search-input"
            autoComplete="off"
          />
          {loading && (
            <div className="absolute right-12 w-5 h-5 border-2 border-brand-cobalt/30 border-t-brand-cobalt rounded-full animate-spin" />
          )}
          {query && !loading && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-4 text-brand-black/40 hover:text-brand-cobalt transition-colors"
              aria-label="Clear search"
            >
              <X size={20} />
            </button>
          )}
        </div>
      </form>

      {/* Suggestions Dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-2 bg-white border border-brand-black/10 rounded-lg shadow-luxury-lg overflow-hidden">
          <div className="max-h-[400px] overflow-y-auto">
            {suggestions.map((suggestion, index) => (
              <Link
                key={suggestion.id}
                to={productPath(suggestion)}
                onClick={() => setShowSuggestions(false)}
                className={cn(
                  "flex items-center gap-3 px-4 py-3 transition-colors border-b border-brand-black/5 last:border-b-0",
                  selectedIndex === index
                    ? "bg-brand-cobalt/10"
                    : "hover:bg-brand-cobalt/5"
                )}
              >
                {/* Product Image */}
                <div className="w-12 h-12 rounded bg-brand-black/5 overflow-hidden flex-shrink-0">
                  <img
                    src={suggestion.image || 'https://images.unsplash.com/photo-1594035910387-fea47794261f?w=100'}
                    alt={suggestion.name}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Product Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-brand-black/60">{suggestion.product_code}</span>
                  </div>
                  <p className={cn(
                    "text-sm font-semibold truncate",
                    suggestion.gender === 'Hombre' ? 'text-brand-cobalt' : 'text-brand-magenta'
                  )}>
                    {suggestion.name}
                  </p>
                  <p className="text-xs text-brand-black/50 truncate">{suggestion.brand}</p>
                </div>

                {/* Price */}
                <div className="text-sm font-medium text-brand-black">
                  {formatPrice(suggestion.price)}
                </div>
              </Link>
            ))}
          </div>

          {/* View All Results */}
          <button
            onClick={handleSubmit}
            className="w-full px-4 py-3 text-sm text-brand-cobalt hover:bg-brand-cobalt/5 transition-colors border-t border-brand-black/10 text-center"
          >
            Ver todos los resultados para "{query}"
          </button>
        </div>
      )}

      {/* No results message */}
      {showSuggestions && query.length >= 2 && suggestions.length === 0 && !loading && (
        <div className="absolute z-50 w-full mt-2 bg-white border border-brand-black/10 rounded-lg shadow-luxury-lg p-4 text-center">
          <p className="text-brand-black/60 text-sm">No se encontraron productos para "{query}"</p>
          <p className="text-brand-black/40 text-xs mt-1">Intenta con otro término de búsqueda</p>
        </div>
      )}
    </div>
  );
};

export default SearchBar;
